import React, { createContext, useEffect, useState } from 'react';
import env from '@travel/env';
import { isClient } from '../utils';

export interface RpCookieContextValue {
  rpCookie: string;
}

interface RpCookieProviderProps {
  children: React.ReactNode;
}

let rpCookieValue: string = '';
let isInitialized: boolean = false;
let messageHandler: ((event: MessageEvent) => void) | null = null;

/**
 * Gets the current Rp cookie value.
 * @returns The Rp cookie string, or empty string if not yet available
 */
function getRpCookie(): string {
  return rpCookieValue;
}

/**
 * Cleans up the Rp cookie resources (event listener and iframe)
 * @param keepInitialized - If true, keeps isInitialized flag (used when cookie was successfully received)
 */
function cleanupRpCookie(keepInitialized: boolean = false): void {
  if (!isClient()) {
    return;
  }

  // Remove event listener if it exists
  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
  }

  // Remove iframe from DOM
  const iframe = document.getElementById('trv-rp-cookie-iframe');
  if (iframe && iframe.parentNode) {
    iframe.parentNode.removeChild(iframe);
  }

  // Only reset isInitialized if we didn't successfully get the cookie
  // This prevents re-initialization if the provider remounts after getting the cookie
  if (!keepInitialized) {
    isInitialized = false;
  }
}

/**
 * Initializes the Rp cookie by creating an iframe and setting up
 * the postMessage event listener. This function is idempotent and SSR-safe.
 *
 * @param onCookieReceived - Optional callback to invoke when cookie is received
 * @returns Cleanup function to remove event listener and iframe
 */
function initializeRpCookie(onCookieReceived?: (cookie: string) => void): () => void {
  // Check if already initialized or not in browser environment
  if (isInitialized || !isClient()) {
    return () => {};
  }

  const travelEnv = env('TRAVEL_ENV');
  const isProd = travelEnv === 'prod';

  const handleMessage = (event: MessageEvent) => {
    // Strict origin validation
    const ALLOWED_ORIGINS = ['https://www.rakuten.co.jp', 'https://r.r10s.jp'];
    if (!ALLOWED_ORIGINS.includes(event.origin)) {
      return;
    }

    try {
      // Handle both string and object data
      const response = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      // Validate response structure
      if (!response || response.type !== 'ratCk' || !response.data) {
        return;
      }

      const cookieValue = response.data.cks;
      if (cookieValue && typeof cookieValue === 'string') {
        rpCookieValue = cookieValue;

        // Update React state if callback is provided
        if (onCookieReceived) {
          onCookieReceived(cookieValue);
        }

        // Clean up: Remove event listener and iframe after receiving cookie
        // Pass true to keep isInitialized flag - prevents re-initialization on remount
        cleanupRpCookie(true);
      }
    } catch (e) {
      if (!isProd) {
        console.error('[RpCookie] Error parsing message:', e);
      }
    }
  };

  // Store handler reference for cleanup
  messageHandler = handleMessage;
  window.addEventListener('message', handleMessage);

  // Create hidden iframe to fetch Rp cookie
  const parentHost = encodeURIComponent(location.protocol + '//' + location.hostname);
  const iframe = document.createElement('iframe');
  // Use r.r10s.jp in PROD and www.rakuten.co.jp in lower environments
  iframe.src = `https://${isProd ? 'r.r10s.jp' : 'www.rakuten.co.jp'}/com/rat/plugin/external/ral-iframe-rakuten.co.jp.html?o-id=${parentHost}`;
  iframe.style.display = 'none';
  iframe.id = 'trv-rp-cookie-iframe';
  iframe.title = 'rat-cookie-retriever';
  iframe.setAttribute('name', 'rat-cookie-retriever');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');

  iframe.onerror = () => {
    cleanupRpCookie();
  };

  document.body.appendChild(iframe);

  isInitialized = true;

  // Return cleanup function that checks if cookie was received
  return () => {
    // If cookie was already received, keep isInitialized true to prevent re-initialization
    // Otherwise, reset everything
    const keepInitialized = rpCookieValue !== '';
    cleanupRpCookie(keepInitialized);
  };
}

const RpCookieContext = createContext<RpCookieContextValue | undefined>(undefined);

/**
 * Provider component that initializes the Rp cookie and provides
 * the cookie value to all child components via React Context.
 *
 * The provider is SSR-safe and will only initialize the service on the client-side.
 *
 * @example
 * ```tsx
 * <RpCookieProvider>
 *   <App />
 * </RpCookieProvider>
 * ```
 */

export const RpCookieProvider: React.FC<RpCookieProviderProps> = ({ children }) => {
  const [rpCookie, setRpCookie] = useState<string>(() => getRpCookie());

  useEffect(() => {
    // Only initialize on client side
    if (!isClient()) {
      return;
    }

    // Initialize the service and pass setRpCookie as callback
    // Returns cleanup function to remove event listener and iframe
    const cleanup = initializeRpCookie(setRpCookie);

    // Return cleanup function for when component unmounts
    return cleanup;
  }, []);

  const value: RpCookieContextValue = {
    rpCookie,
  };

  return <RpCookieContext.Provider value={value}>{children}</RpCookieContext.Provider>;
};

export default RpCookieContext;
